/**
 * Authentication Service
 * Handles all authentication-related API calls
 */

import apiService from './api.service';
import { API_ENDPOINTS } from '../config/api.config';
import { tokenStorage, userStorage } from '../utils/storage';
import {
  RegisterRequest,
  RegisterResponse,
  LoginRequest,
  LoginResponse,
  ChangePasswordRequest,
  ForgotPasswordRequest,
  ResetPasswordRequest,
} from '../types/api.types';
import { User } from '../types';

const mapAuthenticatedUser = (user: LoginResponse['data']['user']): User => ({
  id: user.id,
  name: user.fullName || user.username || user.email,
  email: user.email,
  status: 'online',
  avatar: undefined,
  roles: user.roles || [],
});

class AuthService {
  /**
   * Register a new user
   */
  async register(data: RegisterRequest): Promise<RegisterResponse> {
    const response = await apiService.post<RegisterResponse>(
      API_ENDPOINTS.AUTH.REGISTER,
      data
    );

    // Save tokens and user data
    if (response.token) {
      tokenStorage.setToken(response.token);
      tokenStorage.setRefreshToken(response.refreshToken);
      userStorage.setUser(response.user);
    }

    return response;
  }

  /**
   * Login user
   */
  async login(credentials: LoginRequest): Promise<LoginResponse> {
    const response = await apiService.post<LoginResponse>(
      API_ENDPOINTS.AUTH.LOGIN,
      credentials
    );

    // Save tokens and user data — project only safe fields, never store password_hash
    if (response.data?.token) {
      tokenStorage.setToken(response.data.token);
      userStorage.setUser(mapAuthenticatedUser(response.data.user));
    }

    return response;
  }

  /**
   * Get current user profile
   */
  async getCurrentUser(): Promise<User> {
    const response = await apiService.get<LoginResponse>(API_ENDPOINTS.AUTH.ME);

    const userData = mapAuthenticatedUser(response.data.user);

    // Update stored user data
    userStorage.setUser(userData);

    return userData;
  }

  /**
   * Change password
   */
  async changePassword(data: ChangePasswordRequest): Promise<void> {
    await apiService.put(API_ENDPOINTS.AUTH.CHANGE_PASSWORD, data);
  }

  /**
   * Send password reset email
   */
  async forgotPassword(data: ForgotPasswordRequest): Promise<void> {
    await apiService.post(API_ENDPOINTS.AUTH.FORGOT_PASSWORD, data);
  }

  /**
   * Reset password using token from email link
   */
  async resetPassword(data: ResetPasswordRequest): Promise<void> {
    await apiService.post(API_ENDPOINTS.AUTH.RESET_PASSWORD, data);
  }

  /**
   * Logout user
   */
  async logout(): Promise<void> {
    try {
      await apiService.post(API_ENDPOINTS.AUTH.LOGOUT);
    } catch (error) {
      console.error('Logout API error:', error);
    } finally {
      // Clear all stored data regardless of API response
      tokenStorage.clearAll();
      userStorage.removeUser();
    }
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    return !!tokenStorage.getToken();
  }

  /**
   * Get stored user data
   */
  getStoredUser(): User | null {
    return userStorage.getUser<User>();
  }
}

// eslint-disable-next-line import/no-anonymous-default-export
export default new AuthService();
